import { DateFormats, formatDate } from '@strata/helper-functions';
import { FormPageContainer } from '@strata/react-form-header-and-content';
import { GridWithFilters, PrimaryData, SecondaryData } from '@strata/react-grid';
import { ReferralStatusIndicator } from '@strata/react-status-indicators';
import classNames from 'classnames';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';

import { RTAReferralStatusIndicator } from 'components/referralHistory/RTAReferralStatusIndicator';
import { AppRoutes } from 'services/routes';
import { Grids, useGridState } from 'store/grids/gridsSlice';
import { fetchReferralHistory } from 'store/referralHistory/referralHistorySlice';

const isRowDisabled = (status) =>
    [
        ReferralStatusIndicator.Statuses.BEN_DENIAL,
        ReferralStatusIndicator.Statuses.CANCELLED,
        ReferralStatusIndicator.Statuses.DENIED,
        ReferralStatusIndicator.Statuses.PENDAUTH,
    ].includes(status);

const initialSortProperties = [{ id: 'submittedAt', desc: true }];

// Property name and display text for filter categories
const filterCategories = [
    { key: 'medication', title: 'Medication' },
    { key: 'prescriberName', title: 'Prescriber' },
    {
        key: 'referralStatus',
        title: 'Referral Status',
        valueDisplayTextMap: ReferralStatusIndicator.StatusMessages,
    },
    { key: 'supplierName', title: 'Servicing Supplier' },
];

const customSearchValueHandlers = {
    submittedAt: (value) => formatDate(value, DateFormats.DATE_TIME),
    referralStatus: (value) => ReferralStatusIndicator.StatusMessages[value],
};

const columns = [
    {
        accessorKey: 'patientName',
        header: 'Patient Name',
        size: 120,
        cell: ({ getValue }) => <PrimaryData data={getValue()} />,
    },
    {
        accessorKey: 'prescriberName',
        header: 'Prescriber',
        size: 100,
    },
    {
        accessorKey: 'supplierName',
        header: 'Servicing Supplier',
        size: 100,
    },
    {
        accessorKey: 'medication',
        header: 'Medication',
        size: 100,
    },
    {
        accessorKey: 'submittedAt',
        header: 'Submitted At',
        size: 90,
        cell: ({ getValue }) => (
            <SecondaryData data={formatDate(getValue(), DateFormats.DATE_TIME)} />
        ),
    },
    {
        accessorKey: 'referralStatus',
        header: 'Referral Status',
        size: 140,
        enableSorting: false,
        cell: ({ getValue, row }) => (
            <RTAReferralStatusIndicator
                referralStatus={getValue()}
                supplierName={row.original.supplierName}
                supplierPhone={row.original.supplierPhone}
            />
        ),
    },
];

export const ReferralHistoryPage = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { referralsData, referralsDataLoaded } = useSelector((state) => state.referralHistory);

    useEffect(() => {
        dispatch(fetchReferralHistory());
    }, [dispatch]);

    const {
        sort,
        search,
        activeFilters,
        filterPaneWidth,
        updateGridSort,
        updateGridSearch,
        updateGridActiveFilters,
        updateFilterPaneWidth,
    } = useGridState(Grids.REFERRALS, initialSortProperties);

    return (
        <FormPageContainer>
            <GridWithFilters
                data={referralsData}
                isLoading={!referralsDataLoaded}
                sortProperties={sort}
                onSort={updateGridSort}
                enableSearch
                customSearchValueHandlers={customSearchValueHandlers}
                searchText={search}
                onSearch={updateGridSearch}
                filterCategories={filterCategories}
                activeFilters={activeFilters}
                onFilter={updateGridActiveFilters}
                filterPaneRenderWidth={filterPaneWidth}
                onFilterPaneResize={updateFilterPaneWidth}
                onRowClick={(rowData) => {
                    // Disable click for referrals pending auth
                    if (!isRowDisabled(rowData.referralStatus)) {
                        navigate(AppRoutes.referralDetails(rowData.referralId));
                    }
                }}
                rowCssClass={(rowData) =>
                    classNames({ 'griddle-row--disabled': isRowDisabled(rowData.referralStatus) })
                }
                columns={columns}
            />
        </FormPageContainer>
    );
};
